import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

export interface EducationItem {
  id: string;
  name: string;
}

export interface EducationSelectionDialogData {
  educations: EducationItem[];
  selectedEducation: string; // the selected ID
}

@Component({
  selector: 'app-education-selection-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
  ],
  template: `
    <h2 mat-dialog-title>Vælg uddannelsesvej</h2>
    <div mat-dialog-content>
      <mat-form-field appearance="outline" class="search-field">
        <mat-icon matPrefix>search</mat-icon>
        <input
          matInput
          [(ngModel)]="searchText"
          (input)="filterEducations()"
          placeholder="Søg efter uddannelsesveje..."
        />
        @if (searchText) {
        <button
          matSuffix
          mat-icon-button
          aria-label="Clear"
          (click)="searchText = ''; filterEducations()"
        >
          <mat-icon>close</mat-icon>
        </button>
        }
      </mat-form-field>

      <div class="educations-grid">
        <div
          class="education-card"
          [class.active]="selectedEducationId === 'all' || !selectedEducationId"
          (click)="selectEducation('all')"
        >
          <mat-icon>public</mat-icon>
          <span>Alle uddannelsesveje</span>
        </div>

        @for (education of paginatedEducations; track education) {
        <div
          class="education-card"
          [class.active]="selectedEducationId == education.id"
          (click)="selectEducation(education.id)"
        >
          <mat-icon>school</mat-icon>
          <span>{{ education.name }}</span>
        </div>
        }
      </div>

      <mat-paginator
        [length]="filteredEducations.length"
        [pageSize]="pageSize"
        [pageSizeOptions]="[10, 25, 50]"
        (page)="onPageChange($event)"
      >
      </mat-paginator>
    </div>
    <div mat-dialog-actions align="end">
      <button mat-button (click)="close()">Annuller</button>
      <button mat-raised-button color="primary" (click)="apply()">
        Anvend
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .search-field {
        width: 100%;
        margin-bottom: 16px;
      }

      .educations-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 12px;
        margin-bottom: 16px;
        max-height: min(520px, 58vh);
        overflow-y: auto;
      }

      .education-card {
        background-color: #f5f5f5;
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;

        mat-icon {
          font-size: 24px;
          color: #757575;
        }

        span {
          font-size: 13px;
          font-weight: 500;
          word-break: break-word;
        }

        &:hover {
          background-color: #e0e0e0;
        }

        &.active {
          background-color: #e3f2fd;

          mat-icon {
            color: #1976d2;
          }
        }
      }

      ::ng-deep .mat-mdc-select-panel {
        z-index: 100010 !important;
      }
      ::ng-deep .mat-mdc-menu-panel {
        z-index: 100011 !important;
      }
    `,
  ],
})
export class EducationSelectionDialogComponent implements OnInit {
  searchText = '';
  filteredEducations: EducationItem[] = [];
  paginatedEducations: EducationItem[] = [];
  selectedEducationId: string;

  pageSize = 25;
  currentPage = 0;

  constructor(
    public dialogRef: MatDialogRef<EducationSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EducationSelectionDialogData
  ) {
    this.selectedEducationId = data.selectedEducation ?? 'all';
  }

  ngOnInit(): void {
    this.filteredEducations = [...this.data.educations];
    this.updatePaginatedEducations();
  }

  filterEducations(): void {
    if (!this.searchText) {
      this.filteredEducations = [...this.data.educations];
    } else {
      const searchTerm = this.searchText.toLowerCase();
      this.filteredEducations = this.data.educations.filter((e) =>
        e.name.toLowerCase().includes(searchTerm)
      );
    }
    this.currentPage = 0;
    this.updatePaginatedEducations();
  }

  updatePaginatedEducations(): void {
    const startIndex = this.currentPage * this.pageSize;
    this.paginatedEducations = this.filteredEducations.slice(
      startIndex,
      startIndex + this.pageSize
    );
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedEducations();
  }

  selectEducation(id: string): void {
    this.selectedEducationId = id;
  }

  apply(): void {
    this.dialogRef.close(this.selectedEducationId);
  }

  close(): void {
    this.dialogRef.close();
  }
}
